import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Store } from "lucide-react";
import MerchantAgreement from "@/components/MerchantAgreement";

export default function MerchantSignupForm() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [type, setType] = useState("merchant");
  const [businessName, setBusinessName] = useState("");
  const [description, setDescription] = useState("");
  const [phone, setPhone] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { toast.error("You must read and agree to the Merchant Agreement"); return; }
    setBusy(true);
    try {
      if (!user) {
        const { data } = await api.post("/auth/register", form);
        await login(data.token, data.user);
      }
      await api.post("/merchant/apply", { type, business_name: businessName, description, phone, agree });
      toast.success("Merchant application submitted — we'll notify you once you're approved!");
      navigate("/profile");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="merchant-signup-form">
      {!user && (
        <>
          <input data-testid="merchant-signup-name" required value={form.name} onChange={set("name")} placeholder="Your full name *"
            className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
          <input data-testid="merchant-signup-email" required type="email" value={form.email} onChange={set("email")} placeholder="Email *"
            className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
          <input data-testid="merchant-signup-password" required type="password" minLength={8} value={form.password} onChange={set("password")} placeholder="Password (8+ characters) *"
            className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
        </>
      )}
      <div className="flex gap-2">
        {[["merchant", "Merchant", "Goods & services"], ["card_merchant", "Card Merchant", "Sell sports cards"]].map(([v, l, d]) => (
          <button type="button" key={v} data-testid={`merchant-signup-type-${v}`} onClick={() => setType(v)}
            className={`flex-1 text-left px-3 py-2 border transition-colors ${type === v ? "border-pb-red bg-pb-red/10" : "border-pb-border hover:border-zinc-500"}`}>
            <div className="text-[10px] font-black uppercase">{l}</div>
            <div className="text-[8px] uppercase tracking-widest text-zinc-500">{d}</div>
          </button>
        ))}
      </div>
      <input data-testid="merchant-signup-business" required value={businessName} onChange={(e) => setBusinessName(e.target.value)}
        placeholder="Business / seller name *"
        className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
      <input data-testid="merchant-signup-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Contact phone"
        className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
      <textarea data-testid="merchant-signup-description" value={description} onChange={(e) => setDescription(e.target.value)}
        placeholder="What will you sell through the Bullpen?" rows={2}
        className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
      <MerchantAgreement />
      <label className="flex items-start gap-2 cursor-pointer">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
          className="accent-[#BEF264] w-4 h-4 mt-0.5" data-testid="merchant-signup-agree" />
        <span className="text-xs text-zinc-300">
          I have read and agree to the <span className="font-bold text-white">Pickle Bullpen Merchant Agreement</span> above.
        </span>
      </label>
      <button type="submit" data-testid="merchant-signup-submit" disabled={busy || !agree}
        className="w-full flex items-center justify-center gap-2 bg-pb-red text-black font-black uppercase tracking-widest py-3 text-xs hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Store className="w-4 h-4" />}
        {user ? "Submit Merchant Application" : "Create Account & Apply"}
      </button>
    </form>
  );
}
